import React, { useState, useEffect } from "react";
import { Outlet, Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight, Mail, Phone, MapPin, Instagram, MessageCircle } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { COMPANY, OFFICES } from "./siteContent";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/services", label: "Our Services" },
  { to: "/area-of-expertise", label: "Area of Expertise" },
  { to: "/careers", label: "Careers" },
  { to: "/jobs", label: "Jobs" },
  { to: "/contact", label: "Contact Us" },
];

const FOOTER_COMPANY = [
  { to: "/about", label: "About Emira" },
  { to: "/services", label: "Our Services" },
  { to: "/area-of-expertise", label: "Area of Expertise" },
  { to: "/countries", label: "Countries" },
];
const FOOTER_PLATFORM = [
  { to: "/careers", label: "Careers" },
  { to: "/jobs", label: "Find a Job" },
  { to: "/for-employers", label: "Hire Workforce" },
  { to: "/for-agencies", label: "Register as Agency" },
];

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className={`sticky top-0 z-50 transition-all ${scrolled ? "bg-white/90 backdrop-blur-md shadow-sm border-b border-slate-200" : "bg-white"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo-link"><Logo variant="image" /></Link>
          <nav className="hidden lg:flex items-center gap-0.5">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? "text-[#0088FF]" : "text-slate-600 hover:text-[#0A192F] hover:bg-slate-100"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-2">
            {user ? (
              <Button data-testid="nav-dashboard-btn" onClick={() => nav("/dashboard")} className="bg-[#0A192F] hover:bg-[#0B2545]">My Dashboard <ArrowRight className="h-4 w-4 ml-1" /></Button>
            ) : (
              <>
                <Button variant="ghost" data-testid="nav-login-btn" onClick={() => nav("/login")} className="text-slate-700">Login</Button>
                <Button data-testid="nav-register-btn" onClick={() => nav("/register")} className="bg-[#0088FF] hover:bg-[#0070E0]">Register</Button>
              </>
            )}
          </div>
          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">{open ? <X /> : <Menu />}</button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setOpen(false)}
                className={({ isActive }) => `block px-3 py-2 rounded-lg text-sm font-medium ${isActive ? "text-[#0088FF] bg-slate-50" : "text-slate-700 hover:bg-slate-100"}`}>
                {n.label}
              </NavLink>
            ))}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => nav("/login")}>Login</Button>
              <Button className="flex-1 bg-[#0088FF]" onClick={() => nav("/register")}>Register</Button>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1"><Outlet /></main>

      <footer className="brand-gradient-navy text-slate-300 mt-16">
        <div className="max-w-7xl mx-auto px-6 py-14 grid md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <Logo variant="light" showTagline />
            <p className="mt-4 text-sm text-slate-400 leading-relaxed">{COMPANY.tagline}. End-to-end international manpower recruitment, training and deployment.</p>
            <div className="mt-4 flex gap-3">
              <a href={COMPANY.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="h-9 w-9 rounded-lg bg-white/10 grid place-items-center hover:bg-[#0088FF] transition-colors"><Instagram className="h-4 w-4" /></a>
              <a href={COMPANY.whatsappChannel} target="_blank" rel="noreferrer" aria-label="WhatsApp Channel" data-testid="footer-whatsapp" className="h-9 w-9 rounded-lg bg-white/10 grid place-items-center hover:bg-emerald-500 transition-colors"><MessageCircle className="h-4 w-4" /></a>
              <a href={`mailto:${COMPANY.email}`} aria-label="Email" data-testid="footer-email-icon" className="h-9 w-9 rounded-lg bg-white/10 grid place-items-center hover:bg-[#0088FF] transition-colors"><Mail className="h-4 w-4" /></a>
            </div>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3">Company</h4>
            <ul className="space-y-2 text-sm">{FOOTER_COMPANY.map((l) => <li key={l.to}><Link to={l.to} className="hover:text-white">{l.label}</Link></li>)}</ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3">Platform</h4>
            <ul className="space-y-2 text-sm">{FOOTER_PLATFORM.map((l) => <li key={l.to}><Link to={l.to} className="hover:text-white">{l.label}</Link></li>)}</ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3">Get in touch</h4>
            <div className="space-y-3 text-sm">
              {OFFICES.map((o) => (
                <div key={o.region}>
                  <div className="text-slate-200 font-medium">{o.region}</div>
                  <div className="flex gap-2 text-slate-400 mt-1"><MapPin className="h-4 w-4 text-sky-400 shrink-0 mt-0.5" /><span>{o.address}</span></div>
                  <a href={`tel:${o.phone.replace(/\s/g, "")}`} className="flex gap-2 text-slate-400 mt-1 hover:text-white"><Phone className="h-4 w-4 text-sky-400 shrink-0" /> {o.phone}</a>
                </div>
              ))}
              <a href={`mailto:${COMPANY.email}`} className="inline-block text-sky-300 hover:text-white">{COMPANY.email}</a>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-slate-500">
          <div className="flex flex-wrap justify-center gap-4 mb-2">
            <Link to="/privacy" className="hover:text-white" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white" data-testid="footer-terms">Terms</Link>
            <Link to="/account-deletion" className="hover:text-white" data-testid="footer-account-deletion">Account Deletion</Link>
          </div>
          © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <a href={`https://wa.me/${COMPANY.whatsappNumber}`} target="_blank" rel="noreferrer" data-testid="whatsapp-float"
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white grid place-items-center shadow-xl shadow-emerald-500/30 transition-transform hover:scale-105">
        <MessageCircle className="h-7 w-7" />
      </a>
    </div>
  );
}
